import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Loader2, Check } from 'lucide-react';
import { QuestionnaireQuestion } from '../../services/sowConversationService';

const STANDARD_OPTIONS = {
  NOT_APPLICABLE: "Not Applicable", 
  SKIP: "Skip For Now"
};

interface MultipleChoiceQuestionProps {
  question: QuestionnaireQuestion;
  onContinue: (response: string) => Promise<void>;
  isSubmitting: boolean;
}

export const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  onContinue,
  isSubmitting
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  // Determine if this is a multi-select question
  // For now, default to single select since allowMultiple is not in the API interface
  const allowMultiple = false;

  const handleOptionSelect = (option: string) => {
    if (option === STANDARD_OPTIONS.SKIP) {
      // Skip immediately
      onContinue(option);
    } else {
      if (allowMultiple) {
        // Multi-select mode
        setSelectedOptions(prev => {
          if (prev.includes(option)) {
            return prev.filter(opt => opt !== option);
          } else {
            return [...prev, option];
          }
        });
        setSelectedOption('');
      } else {
        // Single select mode
        setSelectedOption(option);
        setSelectedOptions([]);
      }
    }
  };

  const handleContinue = async () => {
    if (allowMultiple && selectedOptions.length > 0) {
      await onContinue(selectedOptions.join(', '));
      setSelectedOptions([]);
    } else if (selectedOption) {
      await onContinue(selectedOption);
      setSelectedOption('');
    }
  };

  const isStandardOption = (option: string) => {
    return Object.values(STANDARD_OPTIONS).includes(option);
  };

  // Add standard options to the question options
  const allOptions = [...(question.options || []), STANDARD_OPTIONS.NOT_APPLICABLE, STANDARD_OPTIONS.SKIP];

  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-8 relative">
        {/* Skip Button - Upper Right Corner */}
        {allOptions.includes(STANDARD_OPTIONS.SKIP) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOptionSelect(STANDARD_OPTIONS.SKIP)}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            Skip
          </Button>
        )}
        

        {/* Multiple Choice Options */}
        {allOptions && allOptions.length > 0 && (
          <div className="space-y-3 mb-8 pt-8">
            {allowMultiple && (
              <div className="text-sm text-gray-600 mb-4 font-medium">
                Select all that apply:
              </div>
            )}
            {allOptions.map((option, index) => {
              // Skip "Skip For Now" - it gets special treatment
              if (option === STANDARD_OPTIONS.SKIP) {
                return null;
              }
              
              const isSelected = allowMultiple 
                ? selectedOptions.includes(option)
                : selectedOption === option;
              
              return (
                <Button
                  key={index}
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => handleOptionSelect(option)}
                  disabled={isSubmitting}
                  className={`w-full p-4 h-auto text-left justify-between font-medium ${
                    isSelected 
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex-1">{option}</span>
                  {allowMultiple && isSelected && (
                    <Check className="h-4 w-4 ml-2 flex-shrink-0" />
                  )}
                </Button>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center items-center gap-4 pt-6">
          {/* Continue Button - Center */}
          <Button 
            onClick={handleContinue}
            disabled={
              (!selectedOption && selectedOptions.length === 0) || 
              isSubmitting
            }
            size="lg"
            className="px-12 py-3 text-lg font-semibold"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue
                {allowMultiple && selectedOptions.length > 0 && (
                  <span className="ml-2 text-sm">({selectedOptions.length} selected)</span>
                )}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};